import { useState } from 'react';
import {
  INV_PROSPECTS,
  InvLineView,
  InvLogModal,
  InvOverviewView,
  InvProspectsView,
  useToast,
  type InvTabId,
} from '@elva/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInvisHold } from '@/features/ai-team/hooks/useInvisHold';
import { usePeriodRange } from '@/app/hooks/usePeriodRange';

const TAB_PATH: Record<InvTabId, string> = {
  overview: '/ai-team/invisalign',
  prospects: '/ai-team/invisalign/prospects',
  line: '/ai-team/invisalign/line',
};

/**
 * AI Team › Invisalign Coordinator (amendment 30 §3; spec 31 §7 dropped the Call
 * list tab). Three tabs, each with its own URL. The ready patients live in the
 * new Calls section, filtered to Invisalign. Hold shows the held banner + Resume.
 */
export function InvisalignCoordinatorPage({ tab }: { tab: InvTabId }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const { onHold, setHold } = useInvisHold();
  const [period, setPeriod] = usePeriodRange();
  const [logId, setLogId] = useState<string | null>(null);
  const logProspect = logId !== null ? INV_PROSPECTS.find((p) => p.id === logId) : undefined;

  const go = (t: InvTabId) => { void navigate(TAB_PATH[t]); };
  const openSettings = () => { void navigate('/settings/ai/invisalign'); };
  const openPatient = (id: string) => { void navigate(`/patients/${id}/invisalign`); };
  const openCalls = () => { void navigate('/calls?type=invisalign'); };
  const openProspects = (stage?: string) => {
    void navigate(stage !== undefined && stage !== '' ? `/ai-team/invisalign/prospects?stage=${stage}` : '/ai-team/invisalign/prospects');
  };

  const shared = { onSelectTab: go, onOpenSettings: openSettings, onHold, onSetHold: setHold };

  return (
    <>
      {tab === 'overview' ? (
        <InvOverviewView
          {...shared}
          period={period}
          onPeriodChange={setPeriod}
          onOpenCalls={openCalls}
          onOpenProspects={openProspects}
          onEditEmails={() => { void navigate('/settings/ai/invisalign/programme'); }}
          onOpenLine={() => { go('line'); }}
          onOpenCommLog={() => { void navigate('/communication-log?agent=invisalign'); }}
        />
      ) : tab === 'prospects' ? (
        <InvProspectsView
          {...shared}
          initialStage={params.get('stage') ?? undefined}
          onStageChange={openProspects}
          onOpenPatient={openPatient}
          onLogConversation={(id) => { setLogId(id); }}
        />
      ) : (
        <InvLineView
          {...shared}
          onOpenPatient={openPatient}
          onAnswerQuestion={(q) => { void navigate(`/settings/ai/train?q=${encodeURIComponent(q)}`); }}
          onOpenCommLog={() => { void navigate('/communication-log?agent=invisalign&channel=invis-line'); }}
        />
      )}

      <InvLogModal
        open={logId !== null}
        patientName={logProspect?.name ?? 'this patient'}
        stage={logProspect?.stage}
        mode={tab === 'prospects' ? 'before' : 'after'}
        onClose={() => { setLogId(null); }}
        onSave={(sentence) => { setLogId(null); toast({ title: `Logged — ${sentence}`, variant: 'success' }); }}
      />
    </>
  );
}
